// **LE SÉLECTEUR DE GO4LEARN — un seul, sur les quatre espaces.**
//
// Demande du porteur, 2026-09-18, capture du composeur de flashcards à l'appui :
// *« Trouve un style de sélecteur adapté à la dynamique de la charte graphique.
// Tu fais ça partout dans tous les sélecteurs de GO4LEARN. Idéalement tu peux
// utiliser slim select. »*
//
// 🔴 **POURQUOI DU JAVASCRIPT.** La capture montrait la liste NATIVE du système :
// surlignage bleu de l'OS, police de l'OS, coins de l'OS. Un `<option>` n'est pas
// stylable — aucune feuille n'atteint cette liste. Le dessin (`go4learn_select.css`)
// ne peut donc s'appliquer qu'à un composant qui REMPLACE la liste native, et
// SlimSelect est déjà au dépôt (`^2.12.0`), déjà employé par GO4SCHOOL et le
// Pilote. Aucune dépendance nouvelle.
//
// 🔴 **AUCUNE VUE N'EST TOUCHÉE, et c'est un arbitrage, pas une paresse.** Mesuré
// ce jour : **42 `<select>` dans 27 vues** (console, éditorial, studio, apporteur,
// parent, apprenant, inscription). Les annoter un par un, c'est 42 occasions d'en
// oublier un — et le 43ᵉ, écrit demain, n'aurait rien. C'est le raisonnement déjà
// retenu pour l'icône dans le champ (2026-09-17, 59 vues) : *« la règle les couvre
// toutes d'un coup, sans toucher une seule vue »*.
//
// ⚠️ **LE PAQUET EST CHARGÉ À LA DEMANDE, ET CE QUE ÇA GAGNE EST BORNÉ.**
//
// `import()` dynamique : la grande majorité des pages d'un enfant n'a aucun
// `<select>` — « Aujourd'hui », la révision, les défis, La Cour. Sur 3G, ces
// pages-là ne téléchargent donc jamais les **10,1 Ko gz** de SlimSelect ; seuls les
// écrans qui en portent un le paient (flashcards, annotations, examens blancs,
// dépôt de copie).
//
// ⛔ **Ça n'allège en revanche RIEN au budget, et il faut le dire ici** :
// `learn_budget_guard` parcourt la fermeture de l'entrée avec `imports` **et**
// `dynamicImports` (ligne 255). Le cliquet a donc compté les **12,7 Ko** entiers
// — 10,1 SlimSelect + 1,7 de feuille + 1,1 de ce module — et la baseline est
// passée de 177,4 à 190,2 Ko, cible 250. Une garde qu'un `import()` suffirait à
// contourner ne garderait rien ; celle-ci est écrite pour ne pas se laisser faire.

// Le seuil de recherche du Design System (REFERENCE.md §11) : au-delà de 7
// options, une liste se cherche plutôt qu'elle ne se parcourt. Toujours active en
// multi-sélection, où l'on revient plusieurs fois dans la même liste.
const SEARCH_THRESHOLD = 7;

// ⛔ Les échappatoires. Les deux premières sont celles de GO4SCHOOL, reprises
// telles quelles pour qu'une vue partagée se comporte pareil des deux côtés :
// `.no-ss` sur le champ, `.no-ss-group` sur un bloc entier. `data-g4l-select="off"`
// est la nôtre, nommée pour ce produit. La dernière n'est pas une échappatoire
// mais un cas : un `<select size="…">` sans `multiple` est une LISTE DÉROULÉE, pas
// un menu — la remplacer par un menu changerait ce que la vue demande.
const SKIP = (select) =>
  select.classList.contains('no-ss') ||
  select.dataset.g4lSelect === 'off' ||
  select.closest('.no-ss-group') !== null ||
  select.multiple === false && select.size > 1 ||
  // ⛔ **LE SÉLECTEUR DE MOIS DE FLATPICKR — mesuré à l'écran le 2026-09-21.**
  //
  // Le calendrier est injecté dans `<body>` APRÈS le chargement, et l'observateur de
  // mutations ci-dessous rattrape tout `<select>` qui apparaît. Il habillait donc le menu
  // des mois : le natif passait `aria-hidden`, SlimSelect rendait son propre panneau que
  // la feuille de flatpickr ne sait pas placer — **le mois disparaissait de l'en-tête et
  // l'année débordait sur la ligne des jours**.
  //
  // ⚠️ **La règle vit ICI, pas dans le contrôleur de date.** Poser `no-ss` au moment où le
  // calendrier s'ouvre serait une COURSE : l'observateur réagit à l'insertion du nœud, et
  // rien ne garantit l'ordre. Une exception déclarée ne court pas.
  //
  // ⚠️ La suite GO4SCHOOL porte la trace d'une tentative de correctif sur la même collision
  // (`.flatpickr-current-month .ss-mainflatpickr-monthDropdown-months`, dont la virgule a
  // été perdue) : le sélecteur ne désigne rien, et la règle n'a jamais rien fait.
  //
  // ⚠️ **UNE condition, pas deux** : le cliquet de budget apprenant a refusé la seconde
  // (+23 octets gzippés sur un paquet qui n'a plus de marge). Le test de classe était de
  // toute façon redondant — `flatpickr` construit son menu de mois DANS son calendrier, et
  // il n'existe nulle part ailleurs. Une condition qui n'ajoute aucun cas ne vaut pas son
  // poids dans le paquet d'un enfant sur 3G.
  select.closest('.flatpickr-calendar') !== null;

const LABELS = {
  fr: { search: 'Rechercher…', noResults: 'Aucun résultat', choose: 'Choisir…' },
  en: { search: 'Search…', noResults: 'No results', choose: 'Choose…' },
};

// La langue vient de `<html lang>`, posée par le layout depuis `I18n.locale` — le
// produit est bilingue et le sélecteur de langue du chrome recharge la page.
const labels = () => (document.documentElement.lang || 'fr').startsWith('en') ? LABELS.en : LABELS.fr;

// Le paquet n'est demandé qu'une fois par page, même si l'initialisation est
// rejouée (turbo, formulaires imbriqués, trames).
let slimPromise = null;
const loadSlim = () => (slimPromise ||= import('slim-select').then((m) => m.default));

// Les instances vivantes, avec l'habillage d'origine de leur `<select>` : il faut
// pouvoir démonter avant que Turbo ne photographie la page (voir `destroyAll`), et
// rendre alors au champ ce qu'on lui a pris.
const instances = new Map();

function decorate(select, SlimSelect) {
  if (select.dataset.g4lSelectReady === 'true') return;

  const original = { class: select.getAttribute('class'), style: select.getAttribute('style') };

  // 🔴 **ON NE GARDE QUE `g4l-select`, ET C'EST LA CLÉ DE « PARTOUT ».**
  //
  // ⚠️ **Depuis le 2026-09-21, cette classe n'est plus qu'un MARQUEUR.** Le thème et
  // la structure viennent du design system, qui vise `.ss-main:not(.g4sp-select)` —
  // il s'applique quelle que soit la classe recopiée. Ce qui compte ici reste de
  // DÉSHABILLER le champ natif avant construction, pour que son `style=` en ligne ne
  // soit pas recopié sur le PANNEAU.
  //
  // SlimSelect recopie sur `.ss-main` **ET** sur `.ss-content` la classe du
  // `<select>` (`settings.class = selectEl.className.split(' ')`) ET son
  // attribut `style` (`settings.style = selectEl.style.cssText`, appliqué en
  // `cssText` aux deux nœuds, slimselect.es.js l. 417-419 et 1793).
  //
  // ⚠️ **MESURÉ À L'ÉCRAN, le 2026-09-18, avant d'écrire ces lignes** : le
  // composeur de flashcards habille son `<select>` par un `style=` en ligne
  // (`border-radius`, `padding: 0 13px`, `background`) — l'une des ~1 038
  // déclarations en ligne des vues apprenantes. Recopié tel quel, il posait le
  // rayon d'un CHAMP et le rembourrage d'un CHAMP sur le PANNEAU : rayon mesuré
  // à 10 px au lieu des 16 de la carte. Un style en ligne bat toute feuille,
  // donc aucune règle de feuille n'aurait pu le reprendre.
  //
  // ⇒ Le champ natif est déshabillé AVANT construction. Il est de toute façon
  // masqué ensuite ; son habillage servait à corriger une liste native qui
  // n'existe plus. Le composant porte alors UNE seule robe, la même sur les
  // quatre espaces — ce que « partout » veut dire. L'original est gardé pour le
  // démontage.
  select.setAttribute('class', 'g4l-select');
  select.removeAttribute('style');

  const isMultiple = select.multiple;
  const l = labels();

  // Le texte d'attente vient d'abord de la vue (`data-placeholder`), sinon de
  // l'option vide que Rails écrit avec `include_blank:` / `prompt:`. Sans quoi
  // SlimSelect afficherait son propre libellé anglais.
  const firstIsBlank = select.options[0] && select.options[0].value === '';
  const placeholder =
    select.dataset.placeholder || (firstIsBlank ? select.options[0].text.trim() : '') || l.choose;

  // 🔴 **SANS CETTE LIGNE, LE CHAMP S'AFFICHE VIDE — mesuré à l'écran le
  // 2026-09-18 sur le composeur de flashcards.** `include_blank: true` écrit une
  // `<option value="" >` au libellé VIDE, et SlimSelect n'affiche `placeholderText`
  // que si aucune option n'est choisie. Or celle-là l'est : le déclencheur rendait
  // donc une pilule blanche sans un mot, et la liste ouvrait sur une ligne nue
  // portant la seule coche.
  //
  // Le paquet reconnaît l'option d'attente à `data-placeholder="true"`
  // (`getDataFromOption`, slimselect.es.js) — on la lui désigne ici.
  if (firstIsBlank && select.options[0].dataset.placeholder !== 'true') {
    select.options[0].dataset.placeholder = 'true';
    original.blankOption = select.options[0];
  }

  const instance = new SlimSelect({
    select,
    settings: {
      showSearch: isMultiple || select.querySelectorAll('option').length > SEARCH_THRESHOLD,
      searchPlaceholder: l.search,
      searchText: l.noResults,
      placeholderText: placeholder,
      allowDeselect: !isMultiple && firstIsBlank,
      closeOnSelect: !isMultiple,
      hideSelected: false,
      // Le panneau est attaché au `<body>` et positionné en coordonnées de
      // fenêtre : il échappe ainsi à l'`overflow` des cartes, des tableaux et
      // des tiroirs. Sans cela, un sélecteur en bas d'une carte ouvre une liste
      // coupée par le bord de la carte — le défaut corrigé côté GO4SCHOOL.
      contentLocation: document.body,
      contentPosition: 'fixed',
    },
    events: {
      // 🔴 **`input` SEULEMENT, et le compte a été MESURÉ au navigateur.**
      //
      // Les filtres de GO4LEARN se soumettent au changement (`onchange` en ligne
      // dans `parent/learners/new`, `data-action` ailleurs) : il fallait vérifier
      // ce qui leur parvient. Relevé sur le composeur de flashcards, un écouteur
      // posé sur le `<select>` : SlimSelect émet **déjà** un `change` qui bulle
      // (`updateOptions`, slimselect.es.js l. 1612).
      //
      // ⚠️ Émettre le nôtre en plus en faisait compter **trois** — le second
      // réveillait le paquet, qui en réémettait un. Sur un filtre en
      // `this.form.requestSubmit()`, c'étaient trois soumissions pour un choix.
      // Il ne reste donc que `input`, que le paquet n'émet pas. Mesure d'après :
      // **1 `change`, 1 `input`** par choix.
      afterChange: () => {
        select.dispatchEvent(new Event('input', { bubbles: true }));
      },
    },
  });

  select.g4lSelect = instance;
  select.dataset.g4lSelectReady = 'true';
  instances.set(select, original);
}

// A `cloneNode` copy of a decorated field carries the widget SlimSelect drew — with no
// listener behind it — and the ready flag that keeps `initG4lSelects` away: the clone's
// dropdown never opened. Both are taken off, so the clone is decorated anew once inserted.
export function undecorateClone(root) {
  root.querySelectorAll('.ss-main').forEach((node) => node.remove());
  root.querySelectorAll('select').forEach((select) => {
    if (select.dataset.g4lSelectReady !== 'true') return;
    delete select.dataset.g4lSelectReady;
    select.removeAttribute('data-id');
    select.classList.remove('g4l-select');
  });
}

export function initG4lSelects(root = document) {
  const targets = [...root.querySelectorAll('select')].filter(
    (select) => !SKIP(select) && select.dataset.g4lSelectReady !== 'true',
  );
  if (targets.length === 0) return;

  loadSlim().then((SlimSelect) => targets.forEach((select) => decorate(select, SlimSelect)));
}

// ⚠️ **DÉMONTER AVANT LA PHOTO DE TURBO.** Le panneau vit sous `<body>`, hors du
// formulaire : sans ce démontage, l'instantané que Turbo met en cache emporterait
// les panneaux de la page quittée, et le retour arrière les empilerait — des
// listes fantômes qui s'ouvrent sur un champ qui n'existe plus.
function destroyAll() {
  instances.forEach((original, select) => {
    try {
      select.g4lSelect?.destroy();
    } catch (_) {
      /* une instance déjà détachée par Turbo : rien à démonter */
    }
    delete select.g4lSelect;
    delete select.dataset.g4lSelectReady;
    // Rendre au champ son habillage de vue : l'instantané mis en cache est ce que
    // le RETOUR ARRIÈRE affiche avant que le module ne se réinitialise. Sans cette
    // restitution, ce court instant montrerait un `<select>` natif déshabillé.
    if (original.blankOption) delete original.blankOption.dataset.placeholder;
    if (original.class === null) select.removeAttribute('class');
    else select.setAttribute('class', original.class);
    if (original.style === null) select.removeAttribute('style');
    else select.setAttribute('style', original.style);
  });
  instances.clear();
}

export function watchG4lSelects() {
  const run = () => initG4lSelects();

  document.addEventListener('DOMContentLoaded', run);
  document.addEventListener('turbo:load', run);
  document.addEventListener('turbo:frame-load', (event) => initG4lSelects(event.target));
  document.addEventListener('turbo:before-cache', destroyAll);

  // Les formulaires qui POUSSENT des champs : le studio ajoute une question ou
  // une réponse par `rails-nested-form`, la console ouvre des tiroirs. Ces
  // `<select>`-là n'existent au chargement d'aucune page.
  //
  // ⚠️ On n'observe QUE les nœuds ajoutés, et on ne rappelle l'initialisation
  // que si l'un d'eux contient un `<select>` : observer les attributs rappellerait
  // la fonction à chaque classe posée par SlimSelect lui-même — une boucle.
  const observer = new MutationObserver((mutations) => {
    const added = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node.nodeType === 1 &&
          (node.tagName === 'SELECT' || node.querySelector?.('select') !== null),
      ),
    );
    if (added) run();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}
